import { generateCandidateStory } from "./story"; // This is the local deterministic version
import type { CandidateProfile, CandidateStory, TheDMs, TheProof } from "../types";
import type { Opportunity, User } from "../types";
import { GeneratedStoryDraft, IAiClient, IVoiceClient, StructuredSignal } from "../lib/contracts";
import { mockAiClient } from "../lib/providers/ai.mock";
import { SecureAiProxy } from "./proxy";

const useMocks = import.meta.env.VITE_USE_MOCKS === "true";

type StoryInput = {
  user?: User;
  opportunity: Opportunity;
  profile?: CandidateProfile;
  artifacts: TheProof[];
  correspondence: TheDMs[];
  aiSettings?: {
    provider: "local" | "openai" | "anthropic";
    openai_api_key?: string;
    anthropic_api_key?: string;
    api_base_url?: string;
    model?: string; // e.g., gpt-4o-mini, claude-3-opus-20240229
  };
};

class RealAiClient implements IAiClient, IVoiceClient {
  async generateStory(input: StoryInput): Promise<GeneratedStoryDraft> {
    const provider = input.aiSettings?.provider ?? "local";

    if (provider === "local") {
      const result = await generateCandidateStory(input);
      return {
        title: result.title,
        summary: result.summary,
        markdown: result.markdown,
        claims: result.claims,
      };
    }

    // Proxy the request to a secure environment
    return SecureAiProxy.generateStory(input);
  }

  async extractSignals(_text: string): Promise<any> {
    return { names: [], companies: [], locations: [] };
  }

  async extractStructuredSignal(text: string): Promise<StructuredSignal> {
    // Proxy the request to a secure environment
    return SecureAiProxy.extractStructuredSignal(text);
  }

  async transcribe(audioBlob: Blob): Promise<string> {
    // Proxy the request to a secure environment
    return SecureAiProxy.transcribe(audioBlob);
  }
}

export const mockVoiceClient: IVoiceClient = {
  transcribe: async () => {
    return "This is a mock transcription of your voice note. In production, this would use Whisper.";
  },
};

export const aiClient: IAiClient = useMocks ? mockAiClient : new RealAiClient();
export const voiceClient: IVoiceClient = useMocks
  ? mockVoiceClient
  : (aiClient as unknown as IVoiceClient);

/**
 * Provider factory for candidate story generation.
 *
 * Routes to the correct backend based on aiSettings.provider and feature flags.
 * Always falls back to local deterministic generation if cloud provider is
 * unavailable or not configured — preserving the local-first promise.
 */
export async function generateStory(
  input: StoryInput,
): Promise<Omit<CandidateStory, "story_id" | "updated_at">> {
  const generated = await aiClient.generateStory(input);

  return {
    opportunity_id: input.opportunity.opportunity_id,
    title:
      generated.title ||
      `${input.user?.full_name || "Candidate"} story for ${input.opportunity.company_name} (AI Generated)`,
    summary:
      generated.summary ||
      `AI narrative built from ${input.artifacts.length} proof items and ${input.correspondence.length} DMs.`,
    markdown: generated.markdown,
    status: "review",
    source_proof_ids: input.artifacts.map((artifact) => artifact.proof_id),
    source_dm_ids: input.correspondence.map((item) => item.dm_id),
    source_artifact_ids: input.artifacts.map((artifact) => artifact.proof_id),
    source_correspondence_ids: input.correspondence.map((item) => item.dm_id),
    claims: generated.claims,
  };
}
